/**
 * Hosts Tab for Show Edit Page
 *
 * Consolidated view for managing speaker/host configuration:
 * - Host persona selection (or Custom mode)
 * - Voice profiles and indices
 * - Voice and script instructions
 */

import { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import type { HostPersona } from '@musedrops/shared';
import { HostPersonaSelector } from '../../../shared/components/HostPersonaSelector';
import { ShowDynamicsSelector } from '../../../shared/components/ShowDynamicsSelector';
import { VoiceProfileSelector } from '../../../shared/components/VoiceProfileSelector';

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
type ChangeHandler<T> = (value: T) => void;

interface ShowEditHostsTabProps {
  // Dynamics
  dynamicsKey: string | null;
  onDynamicsKeyChange: ChangeHandler<string | null>;

  // Speaker 1 props
  hostPersona1: string | null;
  voiceProfile1: string | null;
  voiceInstructions1: string | null;
  scriptInstructions1: string | null;
  voiceIndex1: number | null;

  // Speaker 2 props
  hostPersona2: string | null;
  voiceProfile2: string | null;
  voiceInstructions2: string | null;
  scriptInstructions2: string | null;
  voiceIndex2: number | null;

  // Change handlers
  onHostPersona1Change: ChangeHandler<string | null>;
  onVoiceProfile1Change: ChangeHandler<string | null>;
  onVoiceInstructions1Change: ChangeHandler<string>;
  onScriptInstructions1Change: ChangeHandler<string>;
  onVoiceIndex1Change: ChangeHandler<number | null>;

  onHostPersona2Change: ChangeHandler<string | null>;
  onVoiceProfile2Change: ChangeHandler<string | null>;
  onVoiceInstructions2Change: ChangeHandler<string>;
  onScriptInstructions2Change: ChangeHandler<string>;
  onVoiceIndex2Change: ChangeHandler<number | null>;

  // All personas for looking up selected persona's instructions
  personas: HostPersona[];
}

export const ShowEditHostsTab = ({
  dynamicsKey,
  onDynamicsKeyChange,

  hostPersona1,
  voiceProfile1,
  voiceInstructions1,
  scriptInstructions1,
  voiceIndex1,

  hostPersona2,
  voiceProfile2,
  voiceInstructions2,
  scriptInstructions2,
  voiceIndex2,

  onHostPersona1Change,
  onVoiceProfile1Change,
  onVoiceInstructions1Change,
  onScriptInstructions1Change,
  onVoiceIndex1Change,

  onHostPersona2Change,
  onVoiceProfile2Change,
  onVoiceInstructions2Change,
  onScriptInstructions2Change,
  onVoiceIndex2Change,

  personas,
}: ShowEditHostsTabProps) => {
  // Warning dialog state
  const [showWarning, setShowWarning] = useState(false);
  const [pendingPersona, setPendingPersona] = useState<{
    speaker: 1 | 2;
    value: string | null;
  } | null>(null);

  // Determine if using custom mode (persona is null)
  const isCustom1 = !hostPersona1;
  const isCustom2 = !hostPersona2;

  // Find selected personas to display their instructions
  const selectedPersona1 = hostPersona1
    ? personas.find(p => p.key === hostPersona1)
    : null;
  const selectedPersona2 = hostPersona2
    ? personas.find(p => p.key === hostPersona2)
    : null;

  // Compute what to display: persona instructions if persona selected, show instructions if custom
  const displayScriptInstructions1 = selectedPersona1
    ? selectedPersona1.scriptInstructions
    : scriptInstructions1;
  const displayVoiceInstructions1 = selectedPersona1
    ? selectedPersona1.voiceInstructions
    : voiceInstructions1;
  const displayScriptInstructions2 = selectedPersona2
    ? selectedPersona2.scriptInstructions
    : scriptInstructions2;
  const displayVoiceInstructions2 = selectedPersona2
    ? selectedPersona2.voiceInstructions
    : voiceInstructions2;

  // Check if there's custom content for a speaker
  const hasCustomContent1 = !!(scriptInstructions1 || voiceInstructions1);
  const hasCustomContent2 = !!(scriptInstructions2 || voiceInstructions2);

  // Wrapped handlers that show warning if needed
  const handlePersona1ChangeWithWarning = (value: string | null) => {
    // If selecting a persona (not Custom) and there's custom content, show warning
    if (value && hasCustomContent1) {
      setPendingPersona({ speaker: 1, value });
      setShowWarning(true);
    } else {
      onHostPersona1Change(value);
    }
  };

  const handlePersona2ChangeWithWarning = (value: string | null) => {
    // If selecting a persona (not Custom) and there's custom content, show warning
    if (value && hasCustomContent2) {
      setPendingPersona({ speaker: 2, value });
      setShowWarning(true);
    } else {
      onHostPersona2Change(value);
    }
  };

  // Confirm warning and apply persona change
  const handleConfirmWarning = () => {
    if (pendingPersona) {
      if (pendingPersona.speaker === 1) {
        onHostPersona1Change(pendingPersona.value);
      } else {
        onHostPersona2Change(pendingPersona.value);
      }
    }
    setShowWarning(false);
    setPendingPersona(null);
  };

  // Cancel warning
  const handleCancelWarning = () => {
    setShowWarning(false);
    setPendingPersona(null);
  };

  return (
    <Box>
      {/* Dynamics Selector */}
      <Box sx={{ mb: 3, maxWidth: '600px' }}>
        <ShowDynamicsSelector
          value={dynamicsKey}
          onChange={onDynamicsKeyChange}
          label="Dynamics"
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 4 }}>
        {/* Speaker 1 Column */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Speaker 1
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Host Persona Selector with fixed height wrapper for alignment */}
            <Box sx={{ height: '120px' }}>
              <HostPersonaSelector
                value={hostPersona1}
                onChange={handlePersona1ChangeWithWarning}
                label="Host Persona"
              />
            </Box>

            {/* Script Instructions */}
            <TextField
              label="Script Instructions"
              multiline
              rows={4}
              value={displayScriptInstructions1 || ''}
              onChange={e => onScriptInstructions1Change(e.target.value)}
              fullWidth
              disabled={!isCustom1}
              InputProps={{
                readOnly: !isCustom1,
              }}
            />

            {/* Voice Instructions */}
            <TextField
              label="Voice Instructions"
              multiline
              rows={2}
              value={displayVoiceInstructions1 || ''}
              onChange={e => onVoiceInstructions1Change(e.target.value)}
              fullWidth
              disabled={!isCustom1}
              InputProps={{
                readOnly: !isCustom1,
              }}
            />

            {/* Voice Profile + Index */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <VoiceProfileSelector
                  value={voiceProfile1 || ''}
                  onChange={onVoiceProfile1Change}
                  label="Voice Profile"
                  required
                />
              </Box>
              <TextField
                label="Index"
                type="number"
                value={voiceIndex1 ?? ''}
                onChange={e => {
                  const val = e.target.value;
                  onVoiceIndex1Change(val === '' ? null : parseInt(val, 10));
                }}
                sx={{ width: '100px' }}
                inputProps={{ min: 0, max: 10 }}
              />
            </Box>
          </Box>
        </Box>

        {/* Speaker 2 Column */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Speaker 2
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Host Persona Selector with fixed height wrapper for alignment */}
            <Box sx={{ height: '120px' }}>
              <HostPersonaSelector
                value={hostPersona2}
                onChange={handlePersona2ChangeWithWarning}
                label="Host Persona"
              />
            </Box>

            {/* Script Instructions */}
            <TextField
              label="Script Instructions"
              multiline
              rows={4}
              value={displayScriptInstructions2 || ''}
              onChange={e => onScriptInstructions2Change(e.target.value)}
              fullWidth
              disabled={!isCustom2}
              InputProps={{
                readOnly: !isCustom2,
              }}
            />

            {/* Voice Instructions */}
            <TextField
              label="Voice Instructions"
              multiline
              rows={2}
              value={displayVoiceInstructions2 || ''}
              onChange={e => onVoiceInstructions2Change(e.target.value)}
              fullWidth
              disabled={!isCustom2}
              InputProps={{
                readOnly: !isCustom2,
              }}
            />

            {/* Voice Profile + Index */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <VoiceProfileSelector
                  value={voiceProfile2 || ''}
                  onChange={onVoiceProfile2Change}
                  label="Voice Profile"
                />
              </Box>
              <TextField
                label="Index"
                type="number"
                value={voiceIndex2 ?? ''}
                onChange={e => {
                  const val = e.target.value;
                  onVoiceIndex2Change(val === '' ? null : parseInt(val, 10));
                }}
                sx={{ width: '100px' }}
                inputProps={{ min: 0, max: 10 }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Warning Dialog */}
      <Dialog open={showWarning} onClose={handleCancelWarning}>
        <DialogTitle>Override Custom Instructions?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Selecting a persona will replace your custom script and voice
            instructions with the persona&apos;s instructions. This action
            cannot be undone.
            <br />
            <br />
            Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelWarning}>Cancel</Button>
          <Button
            onClick={handleConfirmWarning}
            color="primary"
            variant="contained"
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
